import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { update } from 'immutability-helper';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import {
	CalendarDaysIcon,
	CheckBadgeIcon,
	ChevronDownIcon,
	DocumentTextIcon,
	EllipsisHorizontalIcon,
	PencilSquareIcon,
	PlusIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import {
	ChevronRightIcon,
	EllipsisVerticalIcon,
} from '@heroicons/react/20/solid';

import useModal from '../hooks/useModal';
import { classNames, getUserFullName } from '../components/helpers/functions';

import Modal from '../components/Shell/Modal';
import Tabs from '../components/Tabs';
import Badge from '../components/Badge';
import {
	AttributeRow,
	TemplateCheckbox,
	TemplateDate,
	TemplateDropdown,
	TemplateEditor,
	TemplateField,
	TemplateFile,
	TemplateTextarea,
	UpdatedTemplateTextarea,
} from '../components/Attributes/Attributes_old';
import Button from '../components/Button';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import { AvatarGenerated } from '../components/Avatar';
import BetaAttributes, { configurePayload } from '../components/Attributes';

import useAsync from '../hooks/useAsync';
import useRequired from '../hooks/useRequired';
import usePrevious from '../hooks/usePrevious';

import {
	addEvent,
	addTaskToService,
	deleteEvent,
	updateEventStatus,
	updateEvents,
} from '../api';
import {
	addAssetToTask,
	completeTask,
	createEventForTask,
	createSingleServiceData,
	deleteEventForTask,
	deleteService,
	deleteSingleServiceData,
	getService,
	pauseTask,
	removeAssetFromTask,
	removeDocumentFromTask,
	updateEventForTask,
	updateService,
	updateSingleServiceData,
	uploadDocumentToTask,
} from '../api/services';
import {
	getModuleCategories,
	getModuleFields,
	getModuleFieldsBeta,
} from '../api/modules';

import { SimpleAccordian } from '../components/Accordian';

import { Card } from '../components/DragAndDrop/Card';
// TODO: Update to use AssetSelectorBeta
// import SelectAsset from '../components/SelectAsset';
import DetailList, {
	DetailListContainer,
	DetailListDate,
	DetailListItem,
	DetailListOptions,
} from '../components/DetailList/DetailList';
import useSearchParams from '../hooks/useSearchParams';
import useSearchQuery from '../hooks/useSearchQuery';
import { useParams } from 'react-router-dom';
import useLocal from '../hooks/useLocal';
import DataTable from '../components/DataTable';
import { Document, Page } from 'react-pdf';
import { addComment, deleteComment, getComments } from '../api/comment';
import EllipsisDropdown, {
	EllipsisDropdownItem,
} from '../components/EllipsisDropdown';

const getTitle = ({ type, title, company, location }) => {
	if (type === 'person') {
		if (company && title) {
			return `${title} at ${company.companyName}`;
		} else if (company && !title) {
			return `from ${company.companyName}`;
		} else if (!company && title) {
			return title;
		}
	} else if (type === 'company') {
		if (location) {
			return `${location && ` from ${location}`}`;
		}
		return null;
	}
	return null;
};

const MODAL_NAME = 'serviceOverview';
const EVENT_MODULE_ID = process.env.REACT_APP_MODULE_ID_EVENT;
const SERVICE_MODULE_ID = process.env.REACT_APP_MODULE_ID_SERVICE;

const STATUS_OPTIONS = [
	{ label: 'Estimated', value: 'estimated' },
	{ label: 'Scheduled', value: 'scheduled' },
	{ label: 'Completed', value: 'completed' },
	{ label: 'Skip', value: 'skip' },
];

const UpdateStatus = ({
	id,
	onClose,
	content,

	defaultForm,
	event,
	//generateNext,
}) => {
	const [form, setForm] = useState({});
	const [updatedForm, setUpdateForm] = useState({});

	const { open: isOpenCompleteTaskModal, setOpen: onOpenCompleteTaskModal } =
		useModal('taskCompletedModal');

	const { execute: onUpdateEvent, status } = useAsync(updateEvents, {
		immediate: false,
	});

	const { execute: onUpdateEventStatus } = useAsync(updateEventStatus, {
		immediate: false,
	});

	const { value: moduleFields } = useAsync(getModuleFields, {
		defaultPayload: { id: EVENT_MODULE_ID, active: true },
	});

	/*
	defaultForm: {
		date: new Date(
			service?.nextEvent?.scheduledDate
		),
		status: 'scheduled',
		//title: service.name,
		scheduledDateEnd:
			service?.nextEvent
				?.scheduledDateEnd,
		...(service?.occurrences
			? { generateNext: true }
			: {}),
	},

	event: service?.nextEvent,
	*/

	const prevIsOpenCompleteTaskModal = usePrevious(isOpenCompleteTaskModal);

	useEffect(() => {
		if (!isOpenCompleteTaskModal && prevIsOpenCompleteTaskModal) {
			onClose();
		}
	}, [isOpenCompleteTaskModal]);

	useEffect(() => {
		if (id && event && moduleFields?.length) {
			// 1. On update attempt, create update object
			let update = {};
			let actualUpdateObj = {};

			const CONVERTER = {
				'event-title': 'title',
				date: 'date',
				'event-type': 'categoryId',
			};

			// Update update object with event fields
			moduleFields?.map((field) => {
				const key = CONVERTER[field.key] || field?.key;
				if (defaultForm[key]) {
					update[field?.key] = {
						fieldId: field?.id,
						value: defaultForm[key],
					};
				}
			});

			// Custom functions()

			if (defaultForm['status']) {
				update['status'] = { value: defaultForm['status'] };
				actualUpdateObj['status'] = { value: defaultForm['status'] };
			}

			if (event['categoryId']) {
				update['event-type'] = { value: event['categoryId'] };
			}

			if (defaultForm['scheduledDateEnd']) {
				update['start-time'] = {
					value: `${moment(defaultForm['date']).get('h')}:${moment(
						defaultForm['date'],
					).get('m')}`,
				};
				actualUpdateObj['start-time'] = {
					value: `${moment(defaultForm['date']).get('h')}:${moment(
						defaultForm['date'],
					).get('m')}`,
				};
				update['end-time'] = {
					value: `${moment(defaultForm['scheduledDateEnd']).get('h')}:${moment(
						defaultForm['scheduledDateEnd'],
					).get('m')}`,
				};
				actualUpdateObj['end-time'] = {
					value: `${moment(defaultForm['scheduledDateEnd']).get('h')}:${moment(
						defaultForm['scheduledDateEnd'],
					).get('m')}`,
				};
			} else {
				update['all-day-event'] = { value: true };
				actualUpdateObj['all-day-event'] = { value: true };
			}

			if (defaultForm['generateNext']) {
				update['generateNext'] = { value: defaultForm['generateNext'] };
				actualUpdateObj['generateNext'] = {
					value: defaultForm['generateNext'],
				};
			}

			actualUpdateObj['event-title'] = { value: event?.title };

			// Update form with event form
			setForm({ ...update });
			setUpdateForm({ ...actualUpdateObj });
		}
	}, [id, moduleFields, event]);

	// Has All Required
	let activeForm = {};
	const requiredForm = { ...form, ...updatedForm };
	Object.keys(requiredForm)
		.filter((key) => requiredForm[key]?.value)
		.map((key) => (activeForm[key] = requiredForm[key]));

	const hasRequired = useRequired(
		activeForm,
		moduleFields?.filter((field) => field.isRequired).map((field) => field.key),
	);

	const skipModuleKeys = [
		'event-title',
		'event-type',
		'description',
		'assetsList',
		...(updatedForm?.status?.value !== 'scheduled'
			? ['all-day-event', 'start-time', 'end-time']
			: []),
		...(updatedForm?.status?.value === 'completed' ||
		updatedForm?.status?.value === 'skip'
			? ['date']
			: []),
	];

	return (
		<>
			<div className="grid grid-cols-2 gap-4 -mt-3">
				{content === 'updateStatus' && (
					<>
						<TemplateDropdown
							name="Status"
							options={STATUS_OPTIONS}
							value={updatedForm?.status?.value || form?.status?.value}
							onChange={(index, status) => {
								setUpdateForm({ ...updatedForm, status: { value: status } });
							}}
						/>

						{moduleFields
							?.filter((field) => skipModuleKeys.indexOf(field.key) === -1)
							?.map((field) => (
								<BetaAttributes
									field={field}
									onChange={(index, value) => {
										let additional = {};
										if (index === 'all-day-event') {
											if (value === false) {
												additional = {
													['start-time']: form['start-time'] || {
														value: '9:0',
													},
													['end-time']: form['end-time'] || { value: '10:0' },
												};
											}
										}
										setUpdateForm({
											...additional,
											...updatedForm,
											[index]: { value, fieldId: field?.id },
										});
									}}
									form={{ ...form, ...updatedForm }}
									moduleId={EVENT_MODULE_ID}
								/>
							))}
					</>
				)}
			</div>
			<div className="flex justify-end mt-4">
				<Button
					disabled={!hasRequired}
					onClick={() => {
						if (updatedForm?.status?.value === 'completed') {
							// TODO: fill this out properly
							onOpenCompleteTaskModal({
								eventId: event?.id,
								service: { id: event?.serviceId, name: event?.title },
								defaultForm: {
									completedDate: new Date(),
									...(updatedForm?.generateNext ? { generateNext: true } : {}),
								},
							});
						} else if (updatedForm?.status?.value === 'skip') {
							onUpdateEventStatus(
								{
									...updatedForm,
									id: event?.id,
									completedDate: new Date(),
									...(updatedForm?.generateNext ? { generateNext: true } : {}),
									status: 'skip',
									serviceId: event?.serviceId,
								},
								() => onClose(),
							);
						} else {
							onUpdateEvent(
								{
									...configurePayload(updatedForm, moduleFields),
									status: updatedForm?.status?.value,
									id,
								},
								() => onClose(),
							);
						}
					}}
				>
					{status === 'pending' ? 'Updating...' : 'Update'}
				</Button>
			</div>
		</>
	);
};

const getActionMenu = ({ mainLabel, onClick, items = [] } = {}) => {
	if (!mainLabel) {
		return null;
	}

	const hasItems = !!items?.length;
	return (
		<div className="inline-flex rounded-md shadow-sm">
			<button
				type="button"
				className={classNames(
					'relative inline-flex items-center text-xs text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 bg-white px-3 ',
					hasItems ? 'rounded-l-md py-0' : 'rounded-md py-1.5',
				)}
				onClick={() => {
					onClick();
				}}
			>
				{mainLabel}
			</button>
			{hasItems ? (
				<Menu as="div" className="relative -ml-px block">
					<Menu.Button className="relative inline-flex items-center rounded-r-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
						<span className="sr-only">Open options</span>
						<ChevronDownIcon className="h-3 w-3" aria-hidden="true" />
					</Menu.Button>
					<Transition
						as={Fragment}
						enter="transition ease-out duration-100"
						enterFrom="transform opacity-0 scale-95"
						enterTo="transform opacity-100 scale-100"
						leave="transition ease-in duration-75"
						leaveFrom="transform opacity-100 scale-100"
						leaveTo="transform opacity-0 scale-95"
					>
						<Menu.Items className="absolute right-0 z-10 mt-2 -mr-1 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
							<div className="py-1">
								{items.map((item) => (
									<Menu.Item key={item.name}>
										{({ active }) => (
											<div
												className={classNames(
													active
														? 'bg-gray-100 text-gray-900'
														: 'text-gray-700',
													'block px-4 py-2 text-xs cursor-pointer',
												)}
												onClick={() =>
													typeof item.onClick === 'function' && item.onClick()
												}
											>
												{item.name}
											</div>
										)}
									</Menu.Item>
								))}
							</div>
						</Menu.Items>
					</Transition>
				</Menu>
			) : null}
		</div>
	);
};

function capitalizeFirstLetter(string) {
	const str = string.toLowerCase();
	return str.charAt(0).toUpperCase() + str.slice(1);
}

const ServiceOverview = () => {
	const navigate = useNavigate();

	const { get } = useLocal();
	const user = get('user');

	const [isEditingName, setIsEditingName] = useState(false);
	const { id, task } = useParams();
	const [currentTab, setCurrentTab] = useState('details');
	const [addDocumentToContract, setAddDocumentToTask] = useState(false);
	const [documentUploadForm, setDocumentUploadForm] = useState({});

	const [comment, setComment] = useState('');

	const { open: isAddNewTaskModalOpen, setOpen: openAddNewTaskModal } =
		useModal('taskAdd');

	const { execute: onUpdateEventStatus } = useAsync(updateEventStatus, {
		immediate: false,
	});

	const { execute: onUpdateEvent } = useAsync(updateEvents, {
		immediate: false,
	});

	const { execute: onCreateEventForTask } = useAsync(createEventForTask, {
		immediate: false,
		showNotification: { success: 'Task successfully updated' },
	});

	const { execute: onUpdateEventForTask } = useAsync(updateEventForTask, {
		immediate: false,
		showNotification: { success: 'Task successfully updated' },
	});

	const { execute: onComment } = useAsync(addComment, {
		immediate: false,
		showNotification: { success: 'Comment added' },
	});

	const { execute: onDeleteComment } = useAsync(deleteComment, {
		immediate: false,
		showNotification: { success: 'Comment deleted' },
	});

	const { value: comments, execute: refreshComments } = useAsync(getComments, {
		immediate: false,
	});

	const { execute: onUploadDocuments, status: isUploadingDocument } = useAsync(
		uploadDocumentToTask,
		{
			immediate: false,
		},
	);

	const { execute: onDeleteEventForTask } = useAsync(deleteEventForTask, {
		immediate: false,
		showNotification: { success: 'Task successfully updated' },
	});

	const {
		value: moduleFields,
		status: isLoadingModuleFields,
		execute: onRefreshFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: SERVICE_MODULE_ID, active: true },
	});

	const { execute: onEditService } = useAsync(updateService, {
		immediate: false,
		showNotification: { success: 'Task successfully updated' },
	});

	const { execute: onAddAssetToTask } = useAsync(addAssetToTask, {
		immediate: false,
		showNotification: { success: 'Asset successfully added' },
	});

	const { execute: onRemoveAssetFromTask } = useAsync(removeAssetFromTask, {
		immediate: false,
		showNotification: { success: 'Asset successfully removed' },
	});

	const { open: manageAttributesSettings, setOpen: openManageAttributes } =
		useModal('manageAttributes');

	const prevManageAttributes = usePrevious(manageAttributesSettings);
	useEffect(() => {
		if (!manageAttributesSettings && prevManageAttributes) {
			onRefreshFields();
		}
	});

	const { execute: onCreateServiceData } = useAsync(createSingleServiceData, {
		immediate: false,
	});

	const { execute: onUpdateServiceData } = useAsync(updateSingleServiceData, {
		immediate: false,
	});

	const { execute: onDeleteServiceData } = useAsync(deleteSingleServiceData, {
		immediate: false,
	});

	const renderCard = useCallback((card, index, sortableChecklist) => {
		return (
			<Card
				key={card.id}
				index={index}
				id={card.id}
				text={card.text}
				moveCard={moveCard}
				removeCard={(indexToRemove) => {
					const updatedList = [
						...sortableChecklist.filter(
							(item, index) => index !== indexToRemove,
						),
					];
					setSortableChecklist(updatedList);
				}}
			/>
		);
	}, []);

	const moveCard = useCallback((dragIndex, hoverIndex) => {
		setSortableChecklist((prevCards) =>
			update(prevCards, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, prevCards[dragIndex]],
				],
			}),
		);
	}, []);

	const CONVERTER = {
		name: 'name',
		date: 'date',
		categoryId: 'event-type',
		serviceVendor: 'contacts',
	};

	// Filter state
	const [filterSkipped, setFilterSkipped] = useState(true);
	const [summaryModal, setSummaryModal] = useState(false);
	const [sortableChecklist, setSortableChecklist] = useState([]);

	const [addAssetModal, setAddAssetModal] = useState(false);

	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openDeleteModal, setClose: closeDeleteModal } =
		useModal('deleteModal');
	const { open: isOpenTaskCompletedModal, setOpen: onOpenCompleteTaskModal } =
		useModal('taskCompletedModal');
	const {
		open: eventModalOptions,
		setOpen: setOpenEvent,
		setClose: setCloseEvent,
	} = useModal('eventDetails');
	const { setOpen: onOpenContactModal } = useModal('viewContact');

	const handleClose = (skipOnClose, cb) => {
		setFilterSkipped(true);
		if (!skipOnClose && typeof open?.onClose === 'function') {
			open.onClose();
		}
		setClose();
		setSortableChecklist([]);
		setOpenCreateService(false);
		setCurrentTab('details');
	};

	// Fetch Service
	const {
		value: service,
		status,
		execute: refreshService,
	} = useAsync(getService, { immediate: false });

	const { execute: onRemoveDocument } = useAsync(removeDocumentFromTask, {
		immediate: false,
	});

	const { execute: onCompleteTask } = useAsync(completeTask, {
		immediate: false,
	});

	const { setOpen: openNewCategory } = useModal('addCategoryModal');

	const { value: moduleCategories, execute: onRefreshCategories } = useAsync(
		getModuleCategories,
		{
			defaultPayload: { id: EVENT_MODULE_ID },
			silent: true,
		},
	);

	const contractCategoryOptions = moduleCategories
		?.map((category) => ({
			label: category?.name,
			value: category?.id,
		}))
		.sort((a, b) => a?.label?.localeCompare(b?.label));

	useEffect(() => {
		if (open?.id) {
			refreshService({ id: open?.id });
			refreshComments({ entityId: open?.id, entityType: 'task' });
		} else {
			closeDeleteModal();
		}
	}, [open?.id]);

	const prevAddAssetModal = usePrevious(addAssetModal);
	useEffect(() => {
		if (!addAssetModal && prevAddAssetModal) {
			refreshService({ id: open?.id }, null, { silent: true });
		}
	}, [addAssetModal]);

	useEffect(() => {
		if (service) {
			setSortableChecklist(service?.checklist);
		}
	}, [service]);

	const prevIsOpenTaskCompletedModal = usePrevious(isOpenTaskCompletedModal);
	useEffect(() => {
		if (!isOpenTaskCompletedModal && prevIsOpenTaskCompletedModal) {
			refreshService({ id: open?.id }, null, {
				silent: true,
			});
		}
	}, [isOpenTaskCompletedModal]);

	const {
		open: isDeleteModalOpen,
		setOpen: onOpenDeleteModal,
		setClose: onCloseDeleteModal,
	} = useModal('deleteModal');

	// Delete Service
	const { execute: onDeleteService } = useAsync(deleteService, {
		immediate: false,
		showNotification: true,
	});

	const { execute: onDeleteEvent } = useAsync(deleteEvent, {
		immediate: false,
	});

	const { execute: onPauseTask } = useAsync(pauseTask, {
		immediate: false,
	});

	const { execute: onAddTaskToService } = useAsync(addTaskToService, {
		immediate: false,
	});

	const { execute: onAddEvent } = useAsync(addEvent, {
		immediate: false,
	});

	// Internal Modals
	const [openCreateService, setOpenCreateService] = useState(false);

	// Loading Skeleton
	if (status === 'pending') {
		return <LoadingSkeleton />;
	}

	const isNextServiceOverdue = service?.nextEvent
		? moment(
				service?.nextEvent?.status === 'SCHEDULED'
					? service?.nextEvent?.scheduledDate
					: service?.nextEvent?.estimatedDate,
			).isBefore(moment().startOf('day'))
		: false;

	const renderStatus = (service) => {
		if (service?.completed) {
			return (
				<div className="my-2 ml-2">
					<Badge isBeta status={'success'}>
						{`Completed on ${moment(service?.completed).format('MM/DD/YYYY')}`}
					</Badge>
				</div>
			);
		} else if (service?.closed) {
			return (
				<div className="my-2 ml-2">
					<Badge isBeta>Closed</Badge>
				</div>
			);
		} else {
			if (service?.nextEvent) {
				return (
					<DetailListOptions
						options={[
							{ label: 'Estimated', value: 'estimated' },
							{ label: 'Scheduled', value: 'scheduled' },
							{ label: 'Skip', value: 'skip' },
							{ label: 'Completed', value: 'completed' },
						]}
						value={
							service?.nextEvent?.status === 'SCHEDULED'
								? 'scheduled'
								: 'estimated'
						}
						isClearable={false}
						onChange={(value) => {
							if (value?.value === 'scheduled') {
								setOpenCreateService({
									type: 'update',
									modalContent: 'updateStatus',
									defaultForm: {
										date: new Date(service?.nextEvent?.estimatedDate),
										status: 'scheduled',
										...(service?.occurrences
											? {
													generateNext: true,
												}
											: {}),
									},
									event: service?.nextEvent,
								});
							} else if (value?.value === 'estimated') {
								setOpenCreateService({
									type: 'update',
									modalContent: 'updateStatus',
									defaultForm: {
										date: new Date(service?.nextEvent?.estimatedDate),
										status: 'estimated',
										scheduledDateEnd: service?.nextEvent?.estimatedDate,
										...(service?.occurrences
											? {
													generateNext: true,
												}
											: {}),
									},

									event: service?.nextEvent,
								});
							} else if (value?.value === 'skip') {
								onUpdateEventStatus(
									{
										id: service?.nextEvent?.id,
										completedDate: new Date(),
										...(service?.occurrences
											? {
													generateNext: true,
												}
											: {}),
										status: 'skip',
										serviceId: service?.id,
									},
									() => {
										refreshService({
											id: service?.id,
										});
									},
								);
							} else if (value?.value === 'completed') {
								onOpenCompleteTaskModal({
									...(service?.nextEvent
										? {
												eventId: service?.nextEvent?.id,
											}
										: {}),
									service,
									defaultForm: {
										completedDate: new Date(),
										...(service?.occurrences
											? {
													generateNext: true,
												}
											: {}),
									},
								});
							}
						}}
					/>
				);
			}
			return (
				<div className="my-2 ml-2">
					<Badge isBeta>To do</Badge>
				</div>
			);
		}
	};

	const renderDueDate = (service) => {
		const isNextServiceOverdue = service?.nextEvent
			? moment(
					service?.nextEvent?.status === 'SCHEDULED'
						? service?.nextEvent?.scheduledDate
						: service?.nextEvent?.estimatedDate,
				).isBefore(moment().startOf('day'))
			: false;

		return (
			<DetailListDate
				placeholder="Not set"
				value={
					service?.nextEvent?.status === 'SCHEDULED'
						? service?.nextEvent?.scheduledDate
						: service?.nextEvent?.estimatedDate
				}
				type={isNextServiceOverdue ? 'error' : null}
				entity={service}
				onChange={(date) => {
					/*setOpenCreateService({
						type: 'update',
						modalContent: 'updateStatus',
						defaultForm: {
							date: new Date(service?.nextEvent?.estimatedDate),
							status: 'scheduled',
							...(service?.occurrences
								? {
										generateNext: true,
								  }
								: {}),
						},
						event: service?.nextEvent,
					})*/

					if (!date) {
						onDeleteEventForTask(
							{ id: service?.id, eventId: service?.nextEvent?.id },
							() => {
								refreshService({ id: service?.id }, null, {
									silent: true,
								});
							},
						);
					} else if (service?.nextEvent) {
						onUpdateEventForTask(
							{
								eventId: service?.nextEvent?.id,
								id: service?.id,
								date,
							},
							() => {
								refreshService({ id: service?.id }, null, {
									silent: true,
								});
							},
						);
					} else {
						onCreateEventForTask({ id: service?.id, date }, () => {
							refreshService({ id: service?.id }, null, {
								silent: true,
							});
						});
					}
				}}
				onRefresh={refreshService}
				field={{}}
			/>
		);

		return service?.nextEvent ? (
			<div className="flex items-center gap-x-2">
				{isNextServiceOverdue ? (
					<>
						<span className="text-red-700">
							Overdue:{' '}
							{moment(
								service?.nextEvent?.scheduledDate ||
									service?.nextEvent?.estimatedDate,
							).format('MMMM Do, YYYY')}
						</span>{' '}
					</>
				) : (
					`${
						service?.nextEvent?.status === 'ESTIMATED'
							? 'Next Due'
							: 'Scheduled'
					}: ${moment(
						service?.nextEvent?.scheduledDate ||
							service?.nextEvent?.estimatedDate,
					).format('MMMM Do, YYYY')}`
				)}

				{service?.nextEvent?.status === 'SCHEDULED' && (
					<Badge
						isBeta
						type="ghost"
						className="cursor-pointer"
						onClick={() =>
							setOpenEvent({
								id: service?.nextEvent?.id,
							})
						}
					>
						<CalendarDaysIcon className="w-5 h-5 text-gray-700" />{' '}
					</Badge>
				)}
			</div>
		) : (
			<div className="flex items-center">
				<CheckBadgeIcon className="w-10 h-10 text-green-400 mr-3" /> All tasks
				completed
			</div>
		);
	};

	return (
		<>
			<Modal
				size={'3xl'}
				noMargins
				showCloseIcon={false}
				key={MODAL_NAME}
				name={MODAL_NAME}
				onClose={() => handleClose()}
				dialogClassName={'!min-h-96'}
			>
				<div
					className="grid grid-cols-3 text-sm"
					style={{ minHeight: '26rem' }}
				>
					<div className={classNames('col-span-3', 'pb-2')}>
						<div className="flex justify-between items-center px-6 pt-6 pb-3">
							<div className="text-lg font-semibold pr-4 w-full">
								<TemplateField
									value={isEditingName || service?.name}
									isEditing={isEditingName}
									onClick={() => {
										setIsEditingName(service?.name);
									}}
									onBlur={() => {
										setIsEditingName(false);

										if (isEditingName === '') {
											setIsEditingName(service?.name);
										} else if (isEditingName) {
											onEditService(
												{ id: service?.id, name: isEditingName },
												() => {
													refreshService({ id: service?.id }, null, {
														silent: true,
													});
												},
											);
										}
									}}
									autoFocus
									onChange={(index, newName) => setIsEditingName(newName)}
								/>
							</div>

							<div className="flex items-center space-x-4">
								<XMarkIcon
									className="w-7 h-7 text-gray-500 cursor-pointer pr-2"
									onClick={() => handleClose()}
								/>
							</div>
						</div>

						<div className="px-6 pb-6 flex justify-end">
							<Menu as="div" className="relative inline-block text-left">
								<div>
									<Menu.Button className="inline-flex w-full justify-center items-center gap-x-2 rounded-md bg-white px-6 py-2 text-xs font-medium text-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
										Options
										<ChevronDownIcon
											aria-hidden="true"
											className="-mr-1 h-3 w-3 text-gray-400"
										/>
									</Menu.Button>
								</div>

								<Menu.Items
									transition
									className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
								>
									<div className="py-1">
										{/*service?.occurrences !== 'custom' && (
												<>
													{service?.nextEvent && service?.occurrences ? (
														<Menu.Item>
															{({ active }) => (
																<a
																	className={classNames(
																		active
																			? 'hover:bg-gray-100 text-gray-900'
																			: 'text-gray-700',
																		' block px-4 py-2 text-xs cursor-pointer'
																	)}
																	onClick={() => {
																		onPauseTask({
																			id: service?.id,
																			nextEventId: service?.nextEvent?.id,
																		});
																	}}
																>
																	<span>Pause Task</span>
																</a>
															)}
														</Menu.Item>
													) : service?.startDate && service?.occurrences ? (
														<Menu.Item>
															{({ active }) => (
																<a
																	className={classNames(
																		active
																			? 'hover:bg-gray-100 text-gray-900'
																			: 'text-gray-700',
																		' block px-4 py-2 text-xs cursor-pointer'
																	)}
																	onClick={() => {
																		onAddTaskToService(
																			{
																				serviceId: service?.id,
																			},
																			() => {
																				refreshService({ id: open?.id });
																			}
																		);
																	}}
																>
																	<span>Resume Task</span>
																</a>
															)}
														</Menu.Item>
													) : null}
												</>
											)*/}

										{service?.completed || service?.closed ? (
											<Menu.Item>
												{({ active }) => (
													<a
														className={classNames(
															active
																? 'hover:bg-gray-100 text-gray-900'
																: 'text-gray-700',
															' block px-4 py-2 text-xs cursor-pointer',
														)}
														onClick={() => {
															onCompleteTask(
																{
																	id: service?.id,
																	action: 'reopen',
																},
																() => {
																	refreshService({ id: open?.id });
																},
															);
														}}
													>
														<span>Reopen Task</span>
													</a>
												)}
											</Menu.Item>
										) : (
											<Menu.Item>
												{({ active }) => (
													<a
														className={classNames(
															active
																? 'hover:bg-gray-100 text-gray-900'
																: 'text-gray-700',
															' block px-4 py-2 text-xs cursor-pointer',
														)}
														onClick={() => {
															onCompleteTask(
																{
																	id: service?.id,
																	action: 'close',
																	...(service?.nextEvent?.id
																		? { eventId: service?.nextEvent?.id }
																		: {}),
																},
																() => {
																	refreshService({ id: open?.id });
																},
															);
														}}
													>
														<span>Close Task</span>
													</a>
												)}
											</Menu.Item>
										)}

										<Menu.Item>
											<div
												className="block px-4 py-2 text-xs text-red-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 hover:bg-red-50 cursor-pointer"
												onClick={() =>
													openDeleteModal({
														title: 'Are you sure you want to delete this task?',
														confirmText: 'Delete Task',
														onConfirm: () => {
															onDeleteService({ id: service?.id }, () => {
																handleClose();
																navigate('/manage/tasks');
															});
														},
													})
												}
											>
												Delete Task
											</div>
										</Menu.Item>
									</div>
								</Menu.Items>
							</Menu>
							{!service?.completed && !service?.closed && (
								<Button
									onClick={() => {
										onOpenCompleteTaskModal({
											...(service?.nextEvent
												? {
														eventId: service?.nextEvent?.id,
													}
												: {}),
											service,
											defaultForm: {
												completedDate: new Date(),
												...(service?.occurrences
													? {
															generateNext: true,
														}
													: {}),
											},
										});
									}}
								>
									Mark as completed
								</Button>
							)}
						</div>

						<div className="px-6 pb-2 ">
							<div className="pb-4">
								{service?.nextEvent && (
									<>
										<div className="flex items-center">
											<div className=" text-xs text-gray-400 mr-2 pl-2 my-3">
												Next Due
											</div>
											<div className="flex-1 my-2 border-b border-gray-100"></div>
										</div>
										<div className="mb-4">
											<div className="px-6 py-3">
												<h1>
													<div className="flex justify-between items-center">
														<div>
															<div className="mt-1 text-base font-semibold leading-6 text-gray-900">
																{service?.nextEvent && (
																	<div className="flex items-center gap-x-2">
																		{isNextServiceOverdue ? (
																			<>
																				<span className="text-red-700">
																					Overdue:{' '}
																					{moment(
																						service?.nextEvent?.scheduledDate ||
																							service?.nextEvent?.estimatedDate,
																					).format('MMMM Do, YYYY')}
																				</span>{' '}
																			</>
																		) : (
																			`${
																				service?.nextEvent?.status ===
																				'ESTIMATED'
																					? 'Next Due'
																					: 'Scheduled'
																			}: ${moment(
																				service?.nextEvent?.scheduledDate ||
																					service?.nextEvent?.estimatedDate,
																			).format('MMMM Do, YYYY')}`
																		)}
																	</div>
																)}
															</div>
														</div>
													</div>
												</h1>
											</div>
										</div>
									</>
								)}
								<div className="sm:block">
									<div className="border-b border-gray-200">
										<nav aria-label="Tabs" className="-mb-px flex space-x-8">
											{[
												{ name: 'Overview', key: 'details' },
												//												{ name: 'Vendor', key: 'vendor' },
												{
													name: 'Assets',
													key: 'assets',
													count: service?.assets?.length || '0',
												},
												{
													name: 'Documents',
													key: 'documents',
													count: service?.documents?.length || '0',
												},

												//	{ name: 'Notifications', key: 'notifications' },

												{
													name: 'History',
													key: 'history',
													count: service?.events?.filter(
														(event) =>
															event?.status === 'COMPLETED' ||
															event?.status === 'SKIPPED',
													)?.length,
												},
											].map((tab) => (
												<a
													key={tab.name}
													onClick={() => setCurrentTab(tab?.key)}
													aria-current={
														tab?.key === currentTab ? 'page' : undefined
													}
													className={classNames(
														tab?.key === currentTab
															? 'border-indigo-500 text-indigo-600'
															: 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700',
														'flex whitespace-nowrap border-b-2 px-1 py-2 text-xs font-medium cursor-pointer flex items-center',
													)}
												>
													{tab.name}
													{tab.count ? (
														<span
															className={classNames(
																tab?.key === currentTab
																	? 'bg-indigo-100 text-indigo-600'
																	: 'bg-gray-100 text-gray-900',
																'ml-2 hidden rounded-full px-2 py-0.5 text-xxs font-medium md:inline-block',
															)}
														>
															{tab.count}
														</span>
													) : null}
												</a>
											))}
										</nav>
									</div>
								</div>

								{/*
										DETAILS
								*/}
								{currentTab === 'details' && (
									<>
										<div className="mt-4">
											<DetailListContainer>
												<DetailListItem label={'Status'}>
													{renderStatus(service)}
												</DetailListItem>

												<DetailListItem label={'Due date'}>
													{renderDueDate(service)}
												</DetailListItem>
												<DetailListItem label={'Category'}>
													<DetailListOptions
														options={contractCategoryOptions}
														value={service?.defaultEventType}
														isClearable={false}
														onChange={(option) => {
															onEditService(
																{
																	id: service?.id,
																	defaultEventType: option?.value,
																},
																(payload) => {
																	refreshService(
																		{
																			id: service?.id,
																		},
																		null,
																		{
																			silent: true,
																		},
																	);
																},
															);
														}}
														newOptionConfig={{
															label: 'Add a new category',
															onClick: () => {
																openNewCategory({
																	moduleId: EVENT_MODULE_ID,
																	onSubmit: (newCategory) =>
																		onEditService(
																			{
																				id: service?.id,
																				defaultEventType: newCategory?.value,
																			},
																			(payload) => {
																				refreshService(
																					{
																						id: service?.id,
																					},
																					() => onRefreshCategories(),
																					{
																						silent: true,
																					},
																				);
																			},
																		),
																});
															},
														}}
													/>
												</DetailListItem>
												<DetailList
													moduleFields={moduleFields}
													entity={service}
													moduleId={SERVICE_MODULE_ID}
													onUpdate={onEditService}
													onRefresh={refreshService}
													show="default"
													skipFields={[
														'assets',
														'name',
														'serviceStart',
														'defaultEventStatus',
														'defaultEventType',
													]}
													alwaysExtended
													skipContainer
												/>

												<DetailList
													moduleFields={moduleFields}
													entity={service}
													moduleId={SERVICE_MODULE_ID}
													onUpdate={onEditService}
													onRefresh={refreshService}
													skipContainer
												/>
											</DetailListContainer>

											<div className="mt-10">
												<h2 className="text-md">Comments</h2>

												<ul
													role="list"
													className={classNames('space-y-1', 'mt-2 mb-6')}
												>
													{comments?.map((activityItem, activityItemIdx) => (
														<li
															key={activityItem.id}
															className="relative flex gap-x-4 group/item"
														>
															<div
																className={classNames(
																	activityItemIdx === comments?.length + 1
																		? 'h-6'
																		: '-bottom-6',
																	'absolute left-0 top-0 flex w-6 justify-center',
																)}
															>
																<div className="w-px bg-gray-200" />
															</div>

															<>
																<AvatarGenerated
																	id={getUserFullName(activityItem?.user)}
																	size="xxs"
																	className="relative mt-3 h-6 w-6 flex-none rounded-full bg-gray-50"
																/>
																<div className="flex-auto rounded-md px-2 py-2 ring-gray-200">
																	<div className="flex justify-between gap-x-4">
																		<div className="py-0.5 text-xs leading-5 text-gray-500 w-full">
																			<span className="font-medium text-gray-900">
																				{`${activityItem?.user?.firstName} ${activityItem?.user?.lastName}`}
																			</span>
																			<time
																				dateTime={activityItem?.createdAt}
																				className="flex-none py-0.5 text-xs leading-5 text-gray-400 pl-1.5"
																			>
																				{moment(
																					activityItem?.createdAt,
																				).fromNow()}
																			</time>
																		</div>
																		<EllipsisDropdown disappearing>
																			<EllipsisDropdownItem
																				danger
																				onClick={() =>
																					onOpenDeleteModal({
																						onConfirm: () => {
																							onCloseDeleteModal();
																							onDeleteComment(
																								{ id: activityItem?.id },
																								() =>
																									refreshComments({
																										entityId: open?.id,
																										entityType: 'task',
																									}),
																							);
																						},
																						confirmText: 'Delete comment',
																						title:
																							'Are you sure you want to delete this comment?',
																					})
																				}
																			>
																				Delete Comment
																			</EllipsisDropdownItem>
																		</EllipsisDropdown>
																	</div>
																	<p className="text-xs text-gray-700 mt-1">
																		{activityItem.comment}
																	</p>
																</div>
															</>
														</li>
													))}
												</ul>
												<div className="flex space-x-3">
													<div className="flex-shrink-0">
														<div className="relative -ml-1.5">
															<AvatarGenerated
																id={getUserFullName(user)}
																size="xs"
															/>
														</div>
													</div>
													<div className="min-w-0 flex-1">
														<TemplateTextarea
															placeholder="Leave a comment"
															dataIndex="comment"
															onChange={(index, newComment) =>
																setComment(newComment)
															}
															value={comment}
														/>
														<div className="mt-6 flex items-center justify-end space-x-4">
															<Button
																onClick={
																	() => {
																		onComment(
																			{
																				entityType: 'task',
																				entityId: open?.id,
																				comment,
																			},
																			() => {
																				setComment('');
																				refreshComments({
																					entityId: open?.id,
																					entityType: 'task',
																				});
																			},
																		);
																	}
																	/*o(
																		{
																			id: open?.id,
																			comment
																		},
																		() => {
																			setComment('')
																		}
																	)*/
																}
																disabled={!comment}
															>
																Add Comment
															</Button>
														</div>
													</div>
												</div>
											</div>
										</div>
									</>
								)}

								{/*
										ASSETS
								*/}
								{currentTab === 'assets' && (
									<>
										{service?.assets?.length ? (
											<div className="text-xs ml-4 divider-y divider-gray-100 mt-2">
												{service?.assets?.map((asset) => (
													<div
														className="flex items-center py-2 justify-between rounded  px-2"
														onClick={
															() => {}
															//onOpenContactModal({ id: asset?.id })
														}
													>
														<div className="flex items-center ">
															<div
																className=" cursor-text ml-3"
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																}}
															>
																<div className="font-medium text-gray-600">
																	{`${asset?.manufacturer?.name} ${asset?.model?.name}`}
																</div>
																<div className="text-gray-500 text-xxs">
																	{asset?.serialNumber}
																</div>
															</div>
														</div>

														<div>
															<Menu
																as="div"
																className="relative inline-block text-left"
															>
																<div>
																	<Menu.Button className="inline-flex flex items-center w-full justify-center gap-x-2 rounded-md  px-6 py-2 text-xs font-medium text-gray-800 ">
																		<EllipsisVerticalIcon
																			aria-hidden="true"
																			className="-mr-5 h-5 w-5 text-gray-400"
																		/>
																	</Menu.Button>
																</div>
																<Menu.Items
																	transition
																	className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
																>
																	<div className="py-1">
																		<Menu.Item>
																			<div
																				onClick={() => {
																					handleClose(true);
																					navigate(
																						`/assets/${asset?.id}/overview`,
																					);
																				}}
																				className="block px-4 py-2 text-xs text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900  hover:bg-gray-50 cursor-pointer"
																			>
																				View Asset
																			</div>
																		</Menu.Item>
																		<Menu.Item>
																			<div
																				className="block px-4 py-2 text-xs text-red-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 hover:bg-red-50 cursor-pointer"
																				onClick={() =>
																					openDeleteModal({
																						title:
																							'Are you sure you want to remove this asset from this task?',
																						confirmText: 'Remove asset',
																						onConfirm: (deleteForm) => {
																							onRemoveAssetFromTask(
																								{
																									id: service?.id,
																									assetId: asset?.id,
																								},
																								() => {
																									refreshService(
																										{ id: open?.id },
																										null,
																										{
																											silent: true,
																										},
																									);
																									closeDeleteModal();
																								},
																							);
																						},
																					})
																				}
																			>
																				Remove Asset
																			</div>
																		</Menu.Item>
																	</div>
																</Menu.Items>
															</Menu>
														</div>
													</div>
												))}

												<div className="flex justify-end items-center mt-3">
													<Button
														type="secondary"
														onClick={() => {
															setAddAssetModal(true);
														}}
													>
														Add asset
													</Button>
												</div>
											</div>
										) : (
											<div className="py-2 transition duration-200 ease-in-out col-span-2">
												<div className="bg-[#f9f8f8] w-full p-10 flex justify-center items-center flex-col gap-4 text-xs">
													<p>You have not added any assets to this task</p>
													<Button
														type="secondary"
														onClick={() => {
															setAddAssetModal(true);
														}}
													>
														Add asset
													</Button>
												</div>
											</div>
										)}
									</>
								)}

								{/*
										DOCUMENTS
								*/}
								{currentTab === 'documents' && (
									<>
										{service?.documents?.length ? (
											<>
												<DataTable
													disableScroll
													disableTitle
													data={{ rows: service?.documents }}
													columns={[
														{
															header: 'Uploaded on',
															accessorKey: 'createdAt',
															enableSorting: true,
															sortingFn: (a, b) => {
																return moment
																	.utc(a?.original?.createdAt)
																	.diff(moment.utc(b?.original?.createdAt));
															},
															cell: (info) => {
																return info.getValue()
																	? moment(info?.getValue()).format(
																			'MM/DD/YYYY',
																		)
																	: null;
															},
														},

														{
															header: 'Name',
															accessorKey: 'name',
															cell: (info) => {
																return (
																	<div className="text-wrap">
																		{info.getValue() || null}
																	</div>
																);
															},
														},
													]}
													rowActions={[
														{
															name: 'View PDF',
															onClick: ({ original }) => {
																window.open(original?.location, '_blank');
															},
														},
														{
															name: 'Remove from task',
															danger:
																'Are you sure you want to remove this document from the task?',
															confirmText: 'Remove document',
															onClick: ({ original }) =>
																onRemoveDocument(
																	{
																		id: service?.id,
																		documentId: original?.id,
																	},
																	() =>
																		refreshService({ id: open?.id }, null, {
																			silent: true,
																		}),
																	{ silent: true },
																),
														},
													]}
													usePagination={false}
													manualSorting={false}
													useSearch={false}
													useExport={false}
													className={'!mb-0'}
													initialState={{
														sortBy: [
															{
																id: 'createdAt',
																desc: false,
															},
														],
													}}
												/>

												<div className="flex justify-end items-center mt-3">
													<Button
														type="secondary"
														onClick={() => {
															setAddDocumentToTask(true);
														}}
													>
														Add Document
													</Button>
												</div>
											</>
										) : (
											<div className="py-2 transition duration-200 ease-in-out col-span-2">
												<div className="bg-[#f9f8f8] w-full p-10 flex justify-center items-center flex-col gap-4 text-xs">
													<p>No documents attached to this task</p>
													<Button
														type="secondary"
														onClick={() => {
															setAddDocumentToTask(true);
														}}
													>
														Add Document
													</Button>
												</div>
											</div>
										)}
									</>
								)}

								{/*
										NOTIFICATIONS
								*/}
								{currentTab === 'notifications' && <></>}

								{/*
										HISTORY
								*/}
								{currentTab === 'history' && (
									<>
										<div className="text-xs ml-4 divide-y divide-gray-50 max-h-96 overflow-scroll mt-2">
											{service?.events
												?.filter(
													(event) =>
														event?.status === 'COMPLETED' ||
														event?.status === 'SKIPPED',
												)
												.map((event) => (
													<div
														className="flex items-center py-2 justify-between cursor-pointer pr-2"
														onClick={() => setOpenEvent({ id: event?.id })}
													>
														<div className="flex items-center ">
															<div className="mr-6 text-gray-500 text-xxs">
																{moment(event?.completedDate).format(
																	'MM/DD/YYYY',
																)}
															</div>
															<div className="mr-3">
																<div className="text-gray-500">
																	<span className="font-medium text-gray-700">
																		{`${event?.createdByUser?.firstName} ${event?.createdByUser?.lastName} (${event?.createdByUser?.email})`}
																	</span>
																</div>
																<div className="text-gray-500 text-xxs">
																	{event?.estimatedDate
																		? `Originally due: ${moment(
																				event?.estimatedDate,
																			).format('MM/DD/YYYY')}`
																		: ''}
																</div>
															</div>
														</div>
														<div className="flex items-center ">
															<div className="mr-3">
																{event?.status === 'COMPLETED' ? (
																	<Badge isBeta status={'success'}>
																		Completed
																	</Badge>
																) : (
																	<Badge isBeta>Skipped</Badge>
																)}
															</div>
															<ChevronRightIcon className="w-5 h-5 text-gray-500" />
														</div>
													</div>
												))}

											{service?.events?.filter(
												(event) =>
													event?.status === 'COMPLETED' ||
													event?.status === 'SKIPPED',
											)?.length === 0 && (
												<div className="flex justify-center items-center mt-10">
													No past history
												</div>
											)}
										</div>
									</>
								)}

								{service?.contacts?.length ? (
									<SimpleAccordian
										size="xs"
										title="Vendor Information"
										showByDefault
									>
										<div className="text-xs ml-4 divider-y divider-gray-100">
											{service?.contacts?.map((contact) => (
												<div
													className="flex items-center py-2 justify-between cursor-pointer rounded hover:bg-gray-50 px-2"
													onClick={() =>
														onOpenContactModal({ id: contact?.id })
													}
												>
													<div className="flex items-center ">
														<div
															className=" cursor-text ml-3"
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
															}}
														>
															<div className="font-medium text-gray-600">
																{contact?.type === 'company'
																	? contact?.companyName
																	: getUserFullName(contact)}
															</div>
															<div className="text-gray-500 text-xxs">
																{contact?.email}
															</div>
														</div>
													</div>
													<div>
														<ChevronRightIcon className="w-5 h-5 text-gray-500" />
													</div>
												</div>
											))}
										</div>
									</SimpleAccordian>
								) : null}

								{service?.checklist && (
									<SimpleAccordian
										size="xs"
										title="Task Progress"
										showByDefault
										showCount
										count={`${
											sortableChecklist?.filter((option) => option?.completed)
												?.length
										}/${sortableChecklist?.length}`}
									>
										<div className="text-xs ml-4">
											<TemplateCheckbox
												dataIndex={'checklist'}
												labelClassName="text-xs"
												options={sortableChecklist?.map((option) => ({
													label: option.text,
													value: option?.id,
												}))}
												onChange={(index, checklist) => {}}
											/>
										</div>
									</SimpleAccordian>
								)}

								{/*<SimpleAccordian
													size="xs"
													title="Attachments"
													showByDefault
												>
													<TemplateFile />
													<div className="text-xs ml-4 divider-y divider-gray-100">
														<div className="flex items-center py-2 justify-between cursor-pointer rounded hover:bg-gray-50 px-2">
															<div className="flex items-center ">
																<div>
																	<DocumentTextIcon className="w-5 h-5 text-red-400 mr-3" />
																</div>
																<div>
																	<div className="font-medium text-gray-600">
																		Name of document
																	</div>
																	<div className="text-gray-500 text-xxs">
																		PDF - 234MB
																	</div>
																</div>
															</div>
															<div>
																<ChevronRightIcon className="w-5 h-5 text-gray-500" />
															</div>
														</div>
														<div>
															<Button type="secondary" className="text-center">
																Upload another
															</Button>
														</div>
													</div>
															</SimpleAccordian>*/}
							</div>
						</div>
					</div>

					{/*service?.occurrences !== 'none' && (
						<div className="col-span-1 bg-stone-100 px-6 py-2 rounded-r-xl">
							<div className="mt-1 mb-3 flex justify-end items-center gap-x-2 pt-2">
								<XMarkIcon
									className="w-7 h-7 text-gray-500 cursor-pointer pr-2"
									onClick={() => handleClose()}
								/>
							</div>

								{service?.startDate && (
									<div className="py-2">
										<div className="text-xxs text-stone-500">Start date</div>
										<div className="text-xs text-gray-800">
											{moment(service?.startDate).format('MM/DD/YYYY')}
										</div>
									</div>
								)}

								<div className="py-2">
									<div className="text-xxs text-stone-500">Recurrence</div>
									<div className="text-xs text-gray-800">
										{capitalizeFirstLetter(service?.occurrences || 'Manual')}
									</div>
								</div>

								{service?.defaultEventCategory ? (
									<div className="py-2">
										<div className="text-xxs text-stone-500">
											Default Event Type
										</div>
										<div className="text-xs text-gray-800">
											{service?.defaultEventCategory?.name}
										</div>
									</div>
								) : null}
							</div>
						</div>
					)*/}
				</div>
				<Modal
					open={openCreateService}
					onClose={() => setOpenCreateService(false)}
				>
					{openCreateService?.type === 'update' && (
						<UpdateStatus
							id={openCreateService?.event?.id}
							onClose={() => {
								refreshService({ id: open?.id }, null, { silent: true });
								setOpenCreateService(false);
							}}
							content={openCreateService?.modalContent}
							{...openCreateService}
						/>
					)}
				</Modal>

				<Modal
					title="Add asset to task"
					open={addAssetModal}
					onClose={() => setAddAssetModal(false)}
				>
					<AttributeRow>
						{/* TODO: Replace with AssetSelectorBeta */}
						<div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-sm">
							<p className="text-yellow-800">
								Asset selection disabled - needs AssetSelectorBeta integration
							</p>
						</div>
					</AttributeRow>
				</Modal>
			</Modal>

			<Modal
				title="Attach document to task"
				open={addDocumentToContract}
				onClose={() => setAddDocumentToTask(false)}
			>
				{documentUploadForm?.files?.length ? (
					<div className="flex justify-center mb-10">
						<Document
							file={documentUploadForm?.files[0]}
							onLoadSuccess={() => {}}
						>
							<Page pageNumber={1} width={250} />
						</Document>
					</div>
				) : (
					<>
						<TemplateFile
							onChange={(index, files) => {
								onUploadDocuments(
									{
										id: service?.id,
										files,
										bucket: 'local-tasks',
										information: {
											id: service?.id,
										},
									},
									() => {
										refreshService({ id: open?.id });
										setDocumentUploadForm({});
										setAddDocumentToTask(false);
									},
								);
							}}
						/>
					</>
				)}

				<div className="flex justify-end mt-4">
					<Button disabled={!documentUploadForm?.length} onClick={() => {}}>
						{isUploadingDocument === 'pending' ? 'Uploading...' : 'Upload'}
					</Button>
				</div>
			</Modal>
		</>
	);
};

export default ServiceOverview;
